import React from 'react';
import { useNavigate } from 'react-router-dom';
import StatsCard from './components/StatsCard';
import ChartSection from './components/ChartSection';
import StudentList from './components/StudentList';
import TimeLine from 'views/admin/main/applications/Calendar/components/TimeLine';
import DottedLoader from 'components/loaders/DottedLoader';
import { useTeamManagement } from 'hooks/useTeamManagement'; 
import { getUserProfile, fetchUsersAssignedToMentor } from 'api';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const MentorHub = () => {
  const navigate = useNavigate();

  // --- 2. USE THE CENTRALIZED HOOK FOR TEAM/POAP/APPROVAL DATA ---
  const { 
    teamsWithStats, 
    pendingApprovals, 
    poaps,
    isLoading: isTeamManagementLoading,
    error: teamManagementError 
  } = useTeamManagement();

  // --- 3. RESTORE ORIGINAL USER PROFILE AND MENTOR ID LOGIC ---
  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    queryKey: ['userProfile'],
    queryFn: getUserProfile,
    refetchOnWindowFocus: true,
  });

  const user = profileData?.user;
  const mentorId = user?.role === 'mentor' ? user.id : user?.mentor?.id;

  // --- 4. RESTORE ORIGINAL FETCH FOR STUDENT ROSTER COUNT ---
  const { 
    data: usersData, 
    isLoading: usersLoading, 
    error: usersError 
  } = useQuery({
    queryKey: ['usersAssignedToMentor', mentorId],
    queryFn: async () => {
      try {
        return await fetchUsersAssignedToMentor(mentorId);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          console.warn('No users assigned to this mentor. Returning empty array.');
          return [];
        }
        throw error;
      }
    },
    enabled: !!mentorId,
    refetchOnWindowFocus: true,
  });


  if (isTeamManagementLoading || profileLoading || usersLoading) {
    return <DottedLoader text="Loading mentor dashboard..." />;
  }

  if (teamManagementError || profileError || usersError) {
    console.error('Error loading dashboard data:', teamManagementError || profileError || usersError);
    return <div>Error loading data. Please try again later.</div>
  }

  // Data from the hook
  const teams = teamsWithStats || [];
  const approvals = pendingApprovals || [];
  
  // --- 5. USE ORIGINAL LOGIC FOR STATS CARDS ---
  const users = usersData || [];
  const totalTeams = teams.length;
  const userCount = users.length;
  const projectsInProgress = teams.filter(team => team.completedStepsCount > 0 && team.completedStepsCount < team.totalStepsCount).length;
  const completedProjects = teams.filter(team => team.completedStepsCount === team.totalStepsCount).length;

  return (
    <div className="mt-3 grid h-full w-full gap-5 pt-[60px] lg:pt-[20px] rounded-[20px]">
      
      {/* Stats Cards */}
      <div className="col-span-12 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <StatsCard 
          title="Total Teams" 
          value={totalTeams} 
          icon="MdGroup" 
          bgColor="bg-lightPrimary dark:bg-navy-600" 
          iconColor="text-navy-700 dark:text-white"
          onClick={() => navigate('/admin/main/users/users-overview', { state: { activeTab: 'Teams' } })}
          isClickable={true}
        />
        <StatsCard 
          title="Student Roster" 
          value={userCount}
          icon="MdAccessTime" 
          bgColor="bg-lightPrimary dark:bg-navy-600" 
          iconColor="text-navy-700 dark:text-white"
          onClick={() => navigate('/admin/main/users/users-overview', { state: { activeTab: 'Users' } })}
          isClickable={true}
        />
        <StatsCard 
          title="Projects in Progress" 
          value={projectsInProgress}
          icon="MdAutorenew" 
          bgColor="bg-lightPrimary dark:bg-navy-600" 
          iconColor="text-navy-700 dark:text-white"
          isClickable={false}
        />

        <StatsCard 
          title="Leaderboard" 
          value="Rankings" 
          icon="MdBarChart" 
          bgColor="bg-lightPrimary dark:bg-navy-600" 
          iconColor="text-navy-700 dark:text-white"
          onClick={() => navigate('/admin/main/leaderboard')}
          isClickable={true}
        />
      </div>

      {/* Main Content */}
      <div className="col-span-12 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Left Column */}
        <div className="col-span-1 grid gap-5">
          <TimeLine teams= {teams}/> 
          <ChartSection approvals={approvals} />
        </div>

        {/* Right Column */}
        <div className="col-span-2 grid gap-5">
          <StudentList teamData={teams} poaps={poaps || []} loading={isTeamManagementLoading}/>
        </div>
      </div>
    </div>
  );
};

export default MentorHub;